// src/StatsCharts.js
//
// Yetkili paneli grafikleri. Grafik kütüphanesi kullanılmıyor; düz SVG ile
// çizildi (paket boyutu ve tam kontrol için).
//
// Renk seçimi ölçüldü, göz kararı değil: kırmızı/yeşil ikilisi deuteranopide
// ΔE 3.0 veriyordu (renk körü kullanıcı ikisini ayırt edemez), o yüzden kırmızı
// yerine turuncu kullanılıyor (ΔE 7.0). Bu bant yalnızca ikincil kodlamayla
// geçerli olduğundan her dilim ayrıca IKON + DOĞRUDAN ETİKET taşıyor ve
// dilimler arasında 2px yüzey boşluğu var. Renk hiçbir yerde tek başına
// anlam taşımıyor. Ayrıca tablo görünümü mevcut.

import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { tokens } from './theme';

const fmt = (n) => new Intl.NumberFormat('tr-TR').format(n || 0);
const pct = (part, total) => (total ? Math.round((100 * part) / total) : 0);

/* ---------------------------------------------------------------- Donut --- */

function Donut({ segments, total, size = 200, thickness = 26 }) {
  const [hover, setHover] = useState(null);
  const radius = (size - thickness) / 2;
  const circ = 2 * Math.PI * radius;
  const GAP = 2; // dilimler arası yüzey boşluğu (px)

  let offset = 0;
  const arcs = segments.map((s) => {
    const raw = total ? (s.value / total) * circ : 0;
    const len = Math.max(0, raw - GAP);
    const arc = { ...s, len, offset, gapped: raw > 0 && raw <= GAP };
    offset += raw;
    return arc;
  });

  const successPct = pct(segments[0]?.value || 0, total);

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} role="img"
           aria-label={`Toplam ${fmt(total)} işlem, %${successPct} başarılı`}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
                stroke={tokens.line} strokeWidth={thickness} opacity={total ? 0 : 1} />
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {arcs.map((a) => (
            <circle
              key={a.key}
              cx={size / 2} cy={size / 2} r={radius}
              fill="none"
              stroke={a.color}
              strokeWidth={hover === a.key ? thickness + 4 : thickness}
              strokeDasharray={`${a.len} ${circ - a.len}`}
              strokeDashoffset={-a.offset}
              style={{ transition: 'stroke-width 120ms ease', cursor: 'pointer' }}
              onMouseEnter={() => setHover(a.key)}
              onMouseLeave={() => setHover(null)}
            />
          ))}
        </g>
      </svg>

      {/* Merkez: kahraman sayı. Grafiğin tek cümlelik cevabı burada. */}
      <Box sx={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', pointerEvents: 'none'
      }}>
        {hover ? (
          <>
            <Typography sx={{ fontSize: '1.9rem', fontWeight: 700, color: tokens.ink, lineHeight: 1 }}>
              {fmt(segments.find((s) => s.key === hover)?.value)}
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.inkMuted, mt: 0.5, textAlign: 'center', px: 2 }}>
              {segments.find((s) => s.key === hover)?.label}
            </Typography>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: '2.4rem', fontWeight: 700, color: tokens.ink, lineHeight: 1, letterSpacing: '-0.03em' }}>
              %{successPct}
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.inkMuted, mt: 0.5 }}>
              başarılı
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.inkFaint, fontSize: '0.68rem' }}>
              {fmt(total)} işlem
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}

/* --------------------------------------------------------------- Legend --- */

function Legend({ segments, total }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 190 }}>
      {segments.map((s) => {
        const Icon = s.icon;
        return (
          <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* İkon + etiket: renk tek başına anlam taşımasın diye zorunlu. */}
            <Icon sx={{ fontSize: '1.05rem', color: s.color }} />
            <Typography variant="body2" sx={{ color: tokens.ink, flex: 1 }}>
              {s.label}
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.ink, fontWeight: 700 }}>
              {fmt(s.value)}
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.inkMuted, width: 42, textAlign: 'right' }}>
              %{pct(s.value, total)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/* ------------------------------------------------------------- Bar list --- */

function BarList({ items, emptyText }) {
  if (!items.length) {
    return <Typography variant="body2" sx={{ color: tokens.inkMuted }}>{emptyText}</Typography>;
  }
  const max = items[0].count || 1;
  return (
    <Box>
      {items.map((item) => (
        <Box key={item.message} sx={{ mb: 1.75 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: tokens.ink, flex: 1 }}>
              {item.message}
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.ink, fontWeight: 700, flexShrink: 0 }}>
              {fmt(item.count)}
            </Typography>
          </Box>
          <Box sx={{ height: 8, borderRadius: '4px', backgroundColor: tokens.canvas, overflow: 'hidden' }}>
            <Box sx={{
              height: 8,
              borderRadius: '0 4px 4px 0',   // veri ucu yuvarlak, taban çizgisine yaslı
              width: `${Math.max(2, (100 * item.count) / max)}%`,
              backgroundColor: tokens.danger,
            }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/* ------------------------------------------------------------ Trend line -- */

function TrendLine({ daily, height = 150 }) {
  const [hover, setHover] = useState(null);
  const w = 640;
  const pad = { t: 12, r: 12, b: 22, l: 34 };

  const pts = useMemo(() => daily.slice(-45), [daily]);
  if (pts.length < 2) return null;

  const maxY = Math.max(1, ...pts.map((d) => Math.max(d.success || 0, d.error || 0)));
  const x = (i) => pad.l + (i * (w - pad.l - pad.r)) / (pts.length - 1);
  const y = (v) => pad.t + (1 - v / maxY) * (height - pad.t - pad.b);
  const path = (key) => pts.map((d, i) => `${i ? 'L' : 'M'}${x(i)},${y(d[key] || 0)}`).join(' ');

  return (
    <Box sx={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} role="img"
           aria-label="Günlük başarı ve hata sayısı">
        {/* Izgara geri planda kalır */}
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={pad.l} x2={w - pad.r} y1={y(maxY * f)} y2={y(maxY * f)}
                  stroke={tokens.line} strokeWidth="1" />
            <text x={pad.l - 6} y={y(maxY * f) + 3} textAnchor="end"
                  fontSize="9" fill={tokens.inkFaint}>{Math.round(maxY * f)}</text>
          </g>
        ))}
        <path d={path('success')} fill="none" stroke={tokens.success} strokeWidth="2"
              strokeLinejoin="round" strokeLinecap="round" />
        <path d={path('error')} fill="none" stroke={tokens.danger} strokeWidth="2"
              strokeLinejoin="round" strokeLinecap="round" />
        {hover !== null && (
          <>
            <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={height - pad.b}
                  stroke={tokens.inkFaint} strokeWidth="1" strokeDasharray="3 3" />
            {['success', 'error'].map((k) => (
              <circle key={k} cx={x(hover)} cy={y(pts[hover][k] || 0)} r="4.5"
                      fill={k === 'success' ? tokens.success : tokens.danger}
                      stroke="#fff" strokeWidth="2" />
            ))}
          </>
        )}
        {/* Hedef alanları imlecten geniş */}
        {pts.map((d, i) => (
          <rect key={d.day} x={x(i) - 8} y={0} width={16} height={height} fill="transparent"
                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
        ))}
        <text x={pad.l} y={height - 6} fontSize="9" fill={tokens.inkFaint}>{pts[0].day}</text>
        <text x={w - pad.r} y={height - 6} fontSize="9" fill={tokens.inkFaint} textAnchor="end">
          {pts[pts.length - 1].day}
        </text>
      </svg>
      {hover !== null && (
        <Box sx={{
          position: 'absolute', top: 0, left: `${(x(hover) / w) * 100}%`,
          transform: 'translateX(-50%)', backgroundColor: tokens.ink, color: '#fff',
          px: 1.25, py: 0.75, borderRadius: 1, fontSize: '0.72rem', whiteSpace: 'nowrap',
          pointerEvents: 'none', zIndex: 2
        }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>{pts[hover].day}</div>
          <div>Başarılı: {fmt(pts[hover].success)}</div>
          <div>Hatalı: {fmt(pts[hover].error)}</div>
        </Box>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------- The panel -- */

export default function StatsCharts({ stats, days, onDaysChange, loading }) {
  const [showTable, setShowTable] = useState(false);

  const selfRow = useMemo(
    () => (stats?.by_action || []).filter((r) => (r.action || '').includes('password_change'))
      .reduce((acc, r) => ({
        success: acc.success + (r.success || 0),
        error: acc.error + (r.error || 0),
        partial: acc.partial + (r.partial || 0),
      }), { success: 0, error: 0, partial: 0 }),
    [stats]
  );

  const segments = [
    { key: 'success', label: 'Başarılı', value: selfRow.success, color: tokens.success, icon: CheckCircleIcon },
    { key: 'partial', label: 'Kısmi (AD değişti, Google değişmedi)', value: selfRow.partial, color: tokens.partial, icon: RemoveCircleIcon },
    { key: 'error', label: 'Başarısız', value: selfRow.error, color: tokens.danger, icon: ErrorIcon },
  ].filter((s) => s.value > 0 || s.key !== 'partial');

  const total = segments.reduce((a, s) => a + s.value, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
        <Box>
          <Typography variant="overline" sx={{ color: tokens.brand, display: 'block' }}>
            Şifre değiştirme
          </Typography>
          <Typography variant="h5" sx={{ color: tokens.ink }}>Başarı özeti</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {[7, 30, 90].map((d) => (
            <Button key={d} size="small"
                    variant={days === d ? 'contained' : 'outlined'}
                    onClick={() => onDaysChange(d)}>
              {d} gün
            </Button>
          ))}
          <Button size="small" variant="text" onClick={() => setShowTable((v) => !v)}>
            {showTable ? 'Grafik' : 'Tablo'}
          </Button>
        </Box>
      </Box>

      {loading && <Typography variant="body2" sx={{ color: tokens.inkMuted }}>Yükleniyor...</Typography>}

      {!loading && stats && (showTable ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Durum</TableCell>
              <TableCell align="right">Adet</TableCell>
              <TableCell align="right">Oran</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {segments.map((s) => (
              <TableRow key={s.key}>
                <TableCell>{s.label}</TableCell>
                <TableCell align="right">{fmt(s.value)}</TableCell>
                <TableCell align="right">%{pct(s.value, total)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Toplam</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(total)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>%100</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', mb: 4 }}>
            <Donut segments={segments} total={total} />
            <Legend segments={segments} total={total} />
          </Box>

          {stats.daily?.length > 1 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: tokens.ink }}>
                  Günlük seyir
                </Typography>
                {/* İki seri var; dataviz kuralı gereği açıklama zorunlu. */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {[
                    { label: 'Başarılı', color: tokens.success },
                    { label: 'Hatalı', color: tokens.danger },
                  ].map((s) => (
                    <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 14, height: 2, backgroundColor: s.color, borderRadius: 1 }} />
                      <Typography variant="caption" sx={{ color: tokens.inkMuted }}>{s.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <TrendLine daily={stats.daily} />
            </Box>
          )}

          <Typography variant="subtitle2" sx={{ color: tokens.ink, mb: 1.5 }}>
            En sık karşılaşılan hatalar
          </Typography>
          <BarList items={stats.top_errors || []} emptyText="Bu dönemde hata kaydı yok." />
        </>
      ))}
    </Box>
  );
}
